/**
 * ThemedDialog — Layer-1 wrapper for shadcn Dialog.
 *
 * Centering uses physical `left-[50%] translate-x-[-50%]` — both axes are
 * physical, so the same transform centers in LTR and RTL alike (centering is
 * symmetric and must NOT be direction-aware). Using logical `start-[50%]` here
 * resolves to `right:50%` in RTL while `translateX(-50%)` stays physical, which
 * shoves a near-full-width dialog off the left edge (only a sliver shows).
 *
 * @layer-1 — shared UI primitive. Import from here for RTL-aware dialogs.
 */
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "@/shared/lib/utils";

const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogPortal = DialogPrimitive.Portal;
const DialogClose = DialogPrimitive.Close;

/**
 * Modal shape:
 *  - "center" (default): centered card, capped width — confirms, forms, detail pop-ups.
 *  - "sheet": full-screen on mobile, large centered panel on desktop with a blurred
 *    backdrop — wide content like the plan comparison. Both share z-tokens, the
 *    physical (RTL-safe) centering, the dark-aware surface, and one Close affordance.
 */
type DialogVariant = "center" | "sheet";

/* center sits above the scrim's solid black; sheet recedes the page behind a blur. */
const overlayClass: Record<DialogVariant, string> = {
  center: "bg-black/60",
  sheet: "bg-black/30 backdrop-blur-[8px] sm:backdrop-blur-[14px]",
};

const contentClass: Record<DialogVariant, string> = {
  // Physical left-[50%] + translate-x-[-50%] centers in LTR and RTL alike (logical
  // start-[50%] would resolve to right:50% in RTL and shove a wide dialog off-screen).
  // bg-popover (elevated surface) separates the card from the scrim in dark mode,
  // where --background is near-black. w-[calc(100%-2rem)] keeps a 16px gutter each
  // side (24px at sm), matching the FloatingTopBar inset; per-dialog max-w-[…] wins.
  center:
    "left-[50%] top-[50%] grid w-[calc(100%-2rem)] sm:w-[calc(100%-3rem)] max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-popover text-popover-foreground p-6 shadow-lg sm:rounded-lg data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
  // Full-bleed on phones; centered ≤1180px panel from sm up (physical centering).
  sheet:
    "inset-0 w-full overflow-y-auto bg-[var(--background)] sm:inset-auto sm:left-1/2 sm:top-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:max-h-[90vh] sm:w-[min(1180px,calc(100vw-48px))] sm:rounded-2xl sm:shadow-2xl data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
};

const closeClass: Record<DialogVariant, string> = {
  center:
    "absolute end-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground",
  sheet:
    "absolute end-4 top-4 z-10 inline-flex size-11 items-center justify-center rounded-full bg-[var(--surface-low)] text-[var(--text-secondary)] transition hover:bg-[var(--surface-card)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--terracotta)]",
};

// No own DialogPortal here. DialogContent already wraps overlay + content in a
// single portal; rendering the overlay as a plain child *before* Content keeps it
// below the content in DOM order. Re-portaling the overlay appended it to <body>
// AFTER the content, so it painted on top and swallowed every click — buttons were
// unreachable and any click registered as "click outside" → closed.
function DialogOverlay({
  variant = "center",
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay> & { variant?: DialogVariant }) {
  return (
    <DialogPrimitive.Overlay
      className={cn(
        "fixed inset-0 z-[var(--z-scrim)] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        overlayClass[variant],
        className,
      )}
      {...props}
    />
  );
}

function DialogContent({
  className,
  children,
  variant = "center",
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & { variant?: DialogVariant }) {
  return (
    <DialogPortal>
      <DialogOverlay variant={variant} />
      <DialogPrimitive.Content
        className={cn(
          "fixed z-[var(--z-modal)] duration-200 focus:outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          contentClass[variant],
          className,
        )}
        {...props}
      >
        {children}
        <DialogPrimitive.Close className={closeClass[variant]}>
          <X className={variant === "sheet" ? "size-5" : "h-4 w-4"} />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

function DialogHeader({ className, ...props }: React.ComponentPropsWithoutRef<"div">) {
  return (
    <div
      className={cn("flex flex-col space-y-1.5 text-center sm:text-start", className)}
      {...props}
    />
  );
}

function DialogFooter({ className, ...props }: React.ComponentPropsWithoutRef<"div">) {
  return (
    <div
      className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
      {...props}
    />
  );
}

function DialogBody({ className, ...props }: React.ComponentPropsWithoutRef<"div">) {
  return <div className={cn("text-sm text-muted-foreground", className)} {...props} />;
}

const DialogTitle = DialogPrimitive.Title;
const DialogDescription = DialogPrimitive.Description;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogHeader,
  DialogFooter,
  DialogBody,
};
